import React, { useEffect, useState } from "react";

import { CheckCircleIcon, ExclamationCircleIcon, PencilAltIcon } from "@heroicons/react/solid";

import { ITheme } from './../../../models/theme/Theme';
import NoItems from "../../../components/Shared/NoItems";
import { useThemes } from "../../../data/theme/useThemes";
import { PageHeader } from "../../../components/Shared/PageHeader";
import LoadingWheel from "../../../components/Shared/LoadingWheel";
import ActionButton from "../../../components/Shared/ActionButton";
import { ButtonInput } from "../../../components/Shared/Inputs/ButtonInput";
import ActionButtonContainer from "../../../components/Shared/ActionButtonContainer";
import { CreateUpdateThemeFlyout } from "../../../components/Theme/CreateUpdateThemeFlyout";

interface ThemeRowProps extends ITheme {
  onEditClick: () => void;
}

function ThemeRow({ name, active, onEditClick }: ThemeRowProps) {
  return (
    <tr className="hover:bg-gray-100 hover:cursor-pointer">
      <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
        {!active ?
          <ExclamationCircleIcon
            className="flex-shrink-0 mr-1.5 h-8 w-8 text-red-400"
            aria-hidden="true"
          />
          :
          <CheckCircleIcon
            className="flex-shrink-0 mr-1.5 h-8 w-8 text-green-400"
            aria-hidden="true"
          />
        }
      </td>

      <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
        {name}
      </td>
      <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
        <ActionButtonContainer>
          <ActionButton onClick={onEditClick} tooltip="Edit" Icon={PencilAltIcon} />
        </ActionButtonContainer>
      </td>
    </tr>
  );
}

export default function ThemeManager() {
  const { themes } = useThemes(true);
  const [createUpdateThemeOpen, setCreateUpdateThemeOpen] = useState(false);
  const [editTheme, setEditTheme] = useState<ITheme>();

  useEffect(() => {
    document.title = "EstateTrack Admin | Theme Manager";
  }, []);

  const handleEditClick = (theme: ITheme) => {
    setEditTheme(theme);
    setCreateUpdateThemeOpen(true);
  }

  const handleCreateEditClose = () => {
    setCreateUpdateThemeOpen(false);

    if (editTheme) {
      setEditTheme(undefined);
    }
  }

  return <>
    <CreateUpdateThemeFlyout
      open={createUpdateThemeOpen}
      setOpen={handleCreateEditClose}
      theme={editTheme}
    />
    <div className="px-4 sm:px-6 lg:px-8">
      <PageHeader title="Theme Manager" description="View and manage themes here">
        <ButtonInput
          label="Add Theme"
          isSubmit={undefined}
          onClick={() => setCreateUpdateThemeOpen(true)}
          classes={undefined}
          children={undefined}
        />
      </PageHeader>
      <div className="-mx-4 mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg">
        {themes.isLoading ? (
          <div className="flex p-10 bg-white justify-center">
            <LoadingWheel width="w-10" height="w-10" />
          </div>
        ) : themes.data && themes.data.data.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Active
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Name
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {themes.data.data.map((theme) => (
                <ThemeRow
                  key={theme.id}
                  onEditClick={() => handleEditClick(theme)}
                  {...theme}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bg-white p-10">
            {/* @ts-ignore */}
            <NoItems
              itemName="theme"
              plural="Themes"
              onNewClick={() => { }}
            />
          </div>
        )}
      </div>
    </div>
  </>;
}